import React, { useState } from "react";
import { useOrchestratorHealth } from "../hooks/useOrchestratorHealth";

/**
 * Persistent orchestrator config-health banner.
 *
 * Renders nothing when the orchestrator is ok or still loading.
 * Shows a dismissible amber warning strip when /health returns 503 (degraded)
 * so the operator sees config problems immediately on page load — without
 * having to open DevTools, curl the server, or trigger a file upload.
 *
 * The banner re-appears after 5 minutes if the problem persists, so a
 * dismissed-but-unfixed degraded deploy stays visible across reloads.
 */
export default function OrchestratorHealthBanner() {
  const { status, configWarnings, refresh } = useOrchestratorHealth();
  const [dismissed, setDismissed] = useState(false);

  if (status !== "degraded" || dismissed) return null;

  return (
    <div
      role="alert"
      data-testid="orchestrator-health-banner"
      style={{
        background: "#fff3cd",
        borderBottom: "2px solid #ffc107",
        color: "#664d03",
        padding: "10px 16px",
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        fontSize: "0.875rem",
        zIndex: 9000,
        position: "relative",
      }}
    >
      {/* warning icon */}
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ flexShrink: 0, marginTop: "1px" }}
        aria-hidden="true"
      >
        <path
          d="M12 2L1 21h22L12 2z"
          stroke="#856404"
          strokeWidth="2"
          strokeLinejoin="round"
          fill="#ffc107"
          fillOpacity="0.4"
        />
        <line x1="12" y1="9" x2="12" y2="14" stroke="#664d03" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="17.5" r="1" fill="#664d03" />
      </svg>

      <div style={{ flex: 1 }}>
        <strong>Orchestrator config warning</strong> — the server is running but
        one or more critical environment variables are missing or look like
        deploy placeholders. Fix the server <code>.env</code> and redeploy.
        {configWarnings.length > 0 && (
          <ul
            data-testid="orchestrator-health-warnings"
            style={{ margin: "6px 0 0 0", paddingLeft: "1.2em" }}
          >
            {configWarnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        )}
      </div>

      <div style={{ display: "flex", gap: "8px", flexShrink: 0, alignItems: "center" }}>
        <button
          onClick={refresh}
          title="Re-check now"
          style={{
            background: "none",
            border: "1px solid #856404",
            borderRadius: "4px",
            color: "#664d03",
            cursor: "pointer",
            fontSize: "0.8rem",
            padding: "2px 8px",
          }}
        >
          Re-check
        </button>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss orchestrator health warning"
          title="Dismiss (re-appears on next poll)"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: "1.2rem",
            lineHeight: 1,
            color: "#664d03",
            padding: "0 4px",
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
